import {IElement} from '../incase-domain/elements';
import {ELEMENT} from '../incase-domain/elements/base/ElementTypes';
import {uploadImageFile, uploadVideoFile} from '../service/files.service';
import {dataUrlToFile} from './fileUtils';

const MANAGED_MEDIA_PATH = /\/(images|videos|previews)\//;

function isManagedMediaSrc(src: string): boolean {
    return MANAGED_MEDIA_PATH.test(src) || /^(images|videos|previews)\//.test(src);
}

function isDataUrl(src: string): boolean {
    return src.startsWith('data:');
}

async function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read media blob'));
        reader.readAsDataURL(blob);
    });
}

async function convertSrcToBase64(src: string | undefined | null): Promise<string | undefined | null> {
    if (!src) {
        return src;
    }
    if (isDataUrl(src) || !isManagedMediaSrc(src)) {
        return src;
    }
    const response = await fetch(src, {credentials: 'include'});
    if (!response.ok) {
        throw new Error(`Failed to fetch media (${response.status})`);
    }
    return blobToDataUrl(await response.blob());
}

function extensionForMime(mimeType: string): string {
    if (mimeType === 'image/jpeg') return '.jpg';
    if (mimeType === 'image/png') return '.png';
    if (mimeType === 'image/gif') return '.gif';
    if (mimeType === 'image/webp') return '.webp';
    if (mimeType === 'video/mp4') return '.mp4';
    if (mimeType === 'video/webm') return '.webm';
    const subtype = mimeType.split('/')[1];
    return subtype ? `.${subtype}` : '';
}

async function convertBase64ToFileUrl(src: string | undefined | null): Promise<string | undefined | null> {
    if (!src || !isDataUrl(src)) {
        return src;
    }
    const mimeMatch = src.slice(0, src.indexOf(',')).match(/^data:([^;]+);base64$/);
    const mimeType = mimeMatch?.[1] ?? 'application/octet-stream';
    const file = dataUrlToFile(src, `element-import${extensionForMime(mimeType)}`);
    if (mimeType.startsWith('video/')) {
        return (await uploadVideoFile(file)).url;
    }
    return (await uploadImageFile(file)).url;
}

async function mapElementMediaSrcs(
    el: IElement,
    convertSrc: (src: string | undefined | null) => Promise<string | undefined | null>
): Promise<IElement> {
    const result: Record<string, unknown> = {...el};

    if ('imageSrc' in el && typeof (el as {imageSrc?: string}).imageSrc === 'string') {
        result.imageSrc = await convertSrc((el as {imageSrc: string}).imageSrc);
    }

    if ('videoSource' in el && el.videoSource && typeof el.videoSource === 'object' && 'src' in el.videoSource) {
        const {src} = el.videoSource as {src?: string};
        result.videoSource = {...(el.videoSource as object), src: await convertSrc(src)};
    }

    if ('videoSrc' in el && typeof (el as {videoSrc?: string}).videoSrc === 'string') {
        result.videoSrc = await convertSrc((el as {videoSrc: string}).videoSrc);
    }

    if (el.type === ELEMENT.IMAGE_GALLERY && 'images' in el && Array.isArray((el as {images?: unknown}).images)) {
        const {images} = el as {images: {imageSrc?: string}[]};
        result.images = await Promise.all(
            images.map(async (img) => ({
                ...img,
                imageSrc: await convertSrc(img?.imageSrc),
            }))
        );
    }

    if (el.type === ELEMENT.FLIP_CARD && 'flipCardElement' in el && (el as {flipCardElement?: unknown}).flipCardElement) {
        const {flipCardElement} = el as {flipCardElement: Record<string, {imageSrc?: string}>};
        const converted = Object.fromEntries(
            await Promise.all(
                Object.entries(flipCardElement).map(async ([side, fe]) => [side, {...fe, imageSrc: await convertSrc(fe?.imageSrc)}])
            )
        );
        result.flipCardElement = converted;
    }

    if (el.type === ELEMENT.PHOTO_ANSWER_QUESTION && 'answers' in el && Array.isArray((el as {answers?: unknown}).answers)) {
        const {answers} = el as {answers: {imgSrc?: string}[]};
        result.answers = await Promise.all(
            answers.map(async (answer) => ({
                ...answer,
                imgSrc: await convertSrc(answer?.imgSrc),
            }))
        );
    }

    return result as unknown as IElement;
}

export function convertElementMediaToBase64(el: IElement): Promise<IElement> {
    return mapElementMediaSrcs(el, convertSrcToBase64);
}

export function convertElementBase64ToFileUrls(el: IElement): Promise<IElement> {
    return mapElementMediaSrcs(el, convertBase64ToFileUrl);
}
